/**
 * 69.23 (DECISIONS 237 (6), 239, 241): the M113's medium mortar — strictly called-in fire. The owner: "start the mortar as
 * strictly an indirect support vehicle that requires someone to call in fire, as it would force bot behavior to be somewhat
 * specialized in the mobile indirect fire support role". A mortar carrier (a `tracked` armed 'mortar', core/body.ts) fires
 * only on a call, and only set: still `MORTAR.setupSeconds`, its crew in the cargo hatch's seat, rounds in its store, no
 * mission of its own still firing. A call (a squad leader's, the radial's Mortar here) is answered by the side's nearest set
 * mortar that can lay on the point: `MORTAR.rounds` of them inside a circle round the point scattered as a strike's by what
 * the side knows (airstrike.ts airstrikeFix), each laid by the towed guns' solver's high solution (solveLay) at the mortar's
 * speed, the first `laySeconds` after the call, `staggerSeconds` apart. The mission is a strike in flight (`kind: 'mortar'`,
 * its rounds' `gun` the carrier): the strike's warning, flight, burst (× MORTAR.damageScale) and point defence carry it.
 * Pure; nothing runs without a mortar carrier.
 */
import type { AirstrikeInbound, GameState, StrikeLaying, StructureState, Vec3 } from '../types';
import { AIRSTRIKE, CREWMAN, MORTAR } from '../constants/action';
import { PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { G } from '../constants/physics';
import { airstrikeFix, airstrikeHome, along, dispersed, gunReachPeople, layStrike, lineFrom, solveLay } from './airstrike';
import { personSurfaceRadius } from './fineTerrain';
import { structureTeam } from './body';
import { seatUnit } from './seats';
import { isCrewman } from './crewman';
import { personSetOf } from './scaleProfile';
import { SeededRandom } from './random';
import { dacos, dcos, dsin } from './dmath';
import { vecAdd, vecDot, vecNormalize, vecScale } from '../math';

/** How slow a carrier may roll and still count as standing (world units a second, × the person: a creep, not a drive). */
const STILL_PEOPLE_PER_SECOND = 0.2;

/**
 * 69.85 (b): the mortar's muzzle speed on a planet (people a second): MORTAR.speedPeople, capped at MORTAR.topCircular × the
 * strike round's circular speed at the planet's surface (its gravity × the person set's gravityScale × AIRSTRIKE.fall, which
 * the lay flies by) — as 69.77 caps the guns' charges (core/airstrike.ts gunCharges). Past it a round fired steep enough to
 * be the mortar's never came down: on the Story's planet every lay failed, every call was refused "Out of range", and the
 * crewed carrier stood at its post all match without a round. The owner's board keeps 165 (its cap is ~250).
 */
export function mortarSpeed(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): number {
  const planet = state.planets[planetIndex];
  if (!planet) return MORTAR.speedPeople;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const circular = Math.sqrt(AIRSTRIKE.fall * ps.gravityScale * G * planet.mass / planet.radius) / person;
  return Math.min(MORTAR.speedPeople, MORTAR.topCircular * circular);
}

/** The side's mortar carriers standing (armed 'mortar', not wrecked), in id order. */
export function mortarsOf(state: Pick<GameState, 'structures' | 'players'>, team: number): StructureState[] {
  return state.structures.filter(s => s.armament === 'mortar' && s.health > 0 && s.mortar && structureTeam(state as GameState, s) === team).sort((a, b) => a.id - b.id);
}

/** 69.46: the seconds a mortar carrier stands still to be set — MORTAR.setupSeconds, CREWMAN.setup of it with a crewman at its tube (core/crewman.ts). */
export function mortarSetupSeconds(state: Pick<GameState, 'players' | 'config'>, s: Pick<StructureState, 'seats'>): number {
  const k = (s.seats ?? []).findIndex(z => z.mortar), crew = k >= 0 ? seatUnit(s.seats![k]) : null;
  return crew !== null && isCrewman(state, state.players.find(p => p.id === crew)) ? MORTAR.setupSeconds * CREWMAN.setup : MORTAR.setupSeconds;
}

/**
 * 69.67: whether mortar carrier `id` has a mission of its own still firing — a round not yet fired, or one still being laid
 * (§5.32). `exceptOwn` (69.23's own fire): its crew's own missions left out — a leader's call cuts them short.
 */
export function mortarFiring(state: Pick<GameState, 'airstrikes'>, id: number, tick: number, exceptOwn = false): boolean {
  return (state.airstrikes?.inbound ?? []).some(x => x.kind === 'mortar' && !(exceptOwn && x.own) && (x.laying?.mortar?.gun === id || x.rounds.some(r => r.gun === id && r.fireTick > tick)));
}

/**
 * 69.23's own fire (GameConfig.mortarOwnFire; the coordinator, Nov 3: a leader's call during an own mission fires): carrier
 * `id`'s own missions cut short — what is not yet fired (laid or still being laid) dropped and back in its store. One still
 * being laid has fired nothing and goes; one laid whole stays as a strike with its fired rounds alone (none, or those flying
 * on) till its time, as any strike's mark. Nothing own: the same state.
 */
export function cutOwnMissions(state: GameState, id: number, tick: number): GameState {
  const a = state.airstrikes;
  if (!a?.inbound.some(x => x.kind === 'mortar' && x.own && (x.laying?.mortar?.gun === id || x.rounds.some(r => r.gun === id)))) return state;
  let back = 0;
  const inbound: AirstrikeInbound[] = [];
  for (const x of a.inbound) {
    if (!(x.kind === 'mortar' && x.own && (x.laying?.mortar?.gun === id || x.rounds.some(r => r.gun === id)))) { inbound.push(x); continue; }
    // (still being laid: nothing fired yet — every round back, laid, in hand or queued; laid whole: its rounds not yet fired)
    if (x.laying) { back += x.rounds.length + x.laying.queue.length + (x.laying.current ? 1 : 0); continue; }
    const unfired = x.rounds.filter(r => r.fireTick > tick).length;
    back += unfired;
    if (unfired === x.ownLaid) continue;   // (none fired yet: dropped whole — no mark or warning standing for rounds never fired)
    inbound.push({ ...x, rounds: x.rounds.filter(r => r.fireTick <= tick) });   // (its rounds in flight: stepAirstrikes drops it once its last landing is past)
  }
  const structures = back ? state.structures.map(s => (s.id === id && s.mortar ? { ...s, mortar: { ...s.mortar, rounds: s.mortar.rounds + back } } : s)) : state.structures;
  return { ...state, structures, airstrikes: { ...a, inbound } };
}

/**
 * Whether a mortar carrier can answer now: set (still its mortarSetupSeconds), its crew seat held by a living unit of its
 * side, rounds left, no mission of its own still firing (`exceptOwn`: its crew's own missions not counted — 69.23's own fire).
 */
export function mortarSet(state: GameState, s: StructureState, tick: number, exceptOwn = false): boolean {
  if (!s.mortar || s.mortar.rounds <= 0 || s.mortar.stillSince === undefined || (tick - s.mortar.stillSince) * PHYS_DT < mortarSetupSeconds(state, s)) return false;
  const k = (s.seats ?? []).findIndex(z => z.mortar), crew = k >= 0 ? seatUnit(s.seats![k]) : null;
  const team = structureTeam(state, s);
  if (crew === null || !state.players.some(p => p.id === crew && p.alive && p.teamIndex === team)) return false;
  return !mortarFiring(state, s.id, tick, exceptOwn);
}

/** Whether a side's leaders can call its mortar now: a tube of its set — one busy only with its crew's own missions counts (a call cuts them, callMortar). */
export function mortarCallable(state: GameState, team: number, tick: number): boolean {
  return mortarsOf(state, team).some(m => mortarSet(state, m, tick, true));
}

/** Once a tick, with a mortar carrier on the field: each one's still-since — set the tick it stops, cleared the tick it moves (packing at once). The same state when nothing changed. */
export function stepMortars(state: GameState, tick: number): GameState {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  let structures: StructureState[] | null = null;
  state.structures.forEach((s, i) => {
    if (!s.mortar || s.health <= 0) return;
    const still = Math.abs(s.drive?.speed ?? 0) <= STILL_PEOPLE_PER_SECOND * person;
    const since = still ? s.mortar.stillSince ?? tick : undefined;
    if (since === s.mortar.stillSince) return;
    const { stillSince: _s, ...rest } = s.mortar;
    (structures ??= state.structures.slice())[i] = { ...s, mortar: since === undefined ? rest : { ...rest, stillSince: since } };
  });
  return structures ? { ...state, structures } : state;
}

/** Whether mortar carrier `s` can lay on `to` from where it stands (the high solution at its planet's speed, steep enough) — callMortar's test. */
export function mortarLays(state: GameState, s: Pick<StructureState, 'planetIndex' | 'surfaceDir'>, to: Vec3): boolean {
  // (the solve flies the elevations through and costs ms a target, most when it fails — the leaders' refused calls lay
  // 1.8–3.5× the reach out. A cheap no first: past MORTAR.reachSlack × the reach from its footing (on the footing's level
  // sphere: the curvature in it), where the target's ground lies under MORTAR.reachDrop × v²/g below the footing. At 45°
  // and steeper (minElevation) the range over a drop h is R0·cos45·(sin45 + √(sin²45 + 2h/R0)), R0 = v²/g: past 1.5× R0
  // only at h ≥ 0.75 R0, at most 1.37× R0 under 0.5 R0 — a deeper drop is always solved)
  const planet = state.planets[s.planetIndex];
  if (!planet) return false;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, from = vecNormalize(s.surfaceDir), dest = vecNormalize(to);
  const speed = mortarSpeed(state, s.planetIndex), foot = personSurfaceRadius(planet, from, ps);
  const reach = gunReachPeople(state, s.planetIndex, speed, AIRSTRIKE.fall, foot);
  if (dacos(Math.max(-1, Math.min(1, vecDot(from, dest)))) * planet.radius / person > MORTAR.reachSlack * reach) {
    const g = (AIRSTRIKE.fall * ps.gravityScale * G * planet.mass) / (foot * foot), sp = speed * person, r0 = sp * sp / g;   // (v²/g, world units)
    if (foot - personSurfaceRadius(planet, dest, ps) < MORTAR.reachDrop * r0) return false;
  }
  const f = solveLay(state, s.planetIndex, s.surfaceDir, vecNormalize(to), speed, { solution: 'high' });
  return !!f && f.elevation >= MORTAR.minElevation;
}

/**
 * A call for `team`'s mortar on `at`: refused with no set mortar ("No mortar set up") or none that can lay on it ("Out of
 * range"); else the nearest set one that can fires the mission — its rounds spent from its store, the mission queued as a
 * strike in flight (`kind: 'mortar'`). 69.67 (ARCHITECTURE §5.32): with `spread` (the Match's calls) its rounds are laid a
 * step a tick from the call, on each tick's ground, inside MORTAR.laySeconds (core/airstrike.ts layStrike), and announced
 * when the last is in; their rounds taken from the store now and handed back for any that never laid. Without, here and now
 * (the same steps).
 */
export function callMortar(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }, tick: number, opts: { spread?: boolean; own?: boolean } = {}): { state: GameState; strike: AirstrikeInbound } | { refused: string } {
  const planet = state.planets[at.planetIndex];
  if (!planet) return { refused: 'Point at the ground' };
  const called = vecNormalize(at.surfaceDir);
  const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), b))));
  // (69.23's own fire: any call but a crew's own takes a tube busy only with its crew's own missions, cut short below)
  const set = mortarsOf(state, team).filter(s => s.planetIndex === at.planetIndex && mortarSet(state, s, tick, !opts.own)).sort((a, b) => arc(a.surfaceDir, called) - arc(b.surfaceDir, called) || a.id - b.id);
  if (!set.length) return { refused: 'No mortar set up' };
  const speed = mortarSpeed(state, at.planetIndex);   // (69.85 (b): the planet's)
  const lay = (from: Vec3, to: Vec3) => { const f = solveLay(state, at.planetIndex, from, to, speed, { solution: 'high' }); return f && f.elevation >= MORTAR.minElevation ? f : null; };
  let gun = set.find(s => lay(s.surfaceDir, called) !== null);
  if (!gun) return { refused: 'Out of range' };
  if (!opts.own && mortarFiring(state, gun.id, tick)) {
    state = cutOwnMissions(state, gun.id, tick);   // (69.23's own fire: its unfired rounds back in the store, this call fired from it)
    gun = state.structures.find(s => s.id === gun!.id)!;
  }
  const a = state.airstrikes ?? { left: {}, issued: {}, inbound: [], next: 0 };
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  // the mission's centre: the point scattered by what the side knows (the strike's draw), its rounds round it
  const { fix, scatterPeople } = airstrikeFix(state, team, at.planetIndex, called, tick);
  const id = a.next, rng = new SeededRandom(`${state.config.seed}-mortar-${id}`);
  const base = airstrikeHome(state, team), home = base && base.planetIndex === at.planetIndex ? base.surfaceDir : null;
  const line = lineFrom(called, home), side = vecNormalize({ x: called.y * line.z - called.z * line.y, y: called.z * line.x - called.x * line.z, z: called.x * line.y - called.y * line.x });
  const ang = rng.next() * 2 * Math.PI, r = Math.sqrt(rng.next()) * scatterPeople * person;
  const aim = r > 0 ? along(called, vecNormalize(vecAdd(vecScale(line, dcos(ang)), vecScale(side, dsin(ang)))), r, planet.radius) : called;
  const n = Math.min(MORTAR.rounds, gun.mortar!.rounds);
  const first = tick + Math.round(MORTAR.laySeconds / PHYS_DT), stagger = Math.round(MORTAR.staggerSeconds / PHYS_DT);
  // 69.64 (DECISIONS 288): laid for the wind as the strike is (its passes, its gunner's error: a draw of its own), each round
  // dispersed by its range from the carrier — its aims drawn here in turn (a lay draws none), queued to be laid (69.67)
  const error = state.config.wind ? (new SeededRandom(`${state.config.seed}-mortar-wind-${id}`).next() * 2 - 1) * AIRSTRIKE.windError : 0;
  const spread = new SeededRandom(`${state.config.seed}-mortar-spread-${id}`);
  const queue: StrikeLaying['queue'] = [];
  for (let k = 0; k < n; k++) {
    const ra = rng.next() * 2 * Math.PI, rr = Math.sqrt(rng.next()) * MORTAR.radiusPeople * person;
    const aimK = rr > 0 ? along(aim, vecNormalize(vecAdd(vecScale(line, dcos(ra)), vecScale(side, dsin(ra)))), rr, planet.radius) : aim;
    queue.push({ k, aim: aimK, laidFor: dispersed(planet, aimK, gun.surfaceDir, spread), gun: gun.id });
  }
  const laying: StrikeLaying = { queue, first, stagger, error, drift: 0, mortar: { gun: gun.id, from: gun.surfaceDir, speed }, spent: { rounds: n } };
  const strike0: AirstrikeInbound = { id, team, planetIndex: at.planetIndex, aim, called, calledTick: tick, impactTick: first, fix, scatterPeople, radiusPeople: MORTAR.radiusPeople, rounds: [], kind: 'mortar', ...(opts.own ? { own: true as const } : {}), laying };
  const spend = (k: number) => state.structures.map(s => (s.id === gun.id ? { ...s, mortar: { ...s.mortar!, rounds: s.mortar!.rounds - k } } : s));
  if (opts.spread) return { state: { ...state, structures: spend(n), airstrikes: { ...a, inbound: [...a.inbound, strike0], next: id + 1 } }, strike: strike0 };
  let strike: AirstrikeInbound | null = strike0;
  while (strike?.laying) strike = layStrike(state, strike).strike;
  if (!strike) return { refused: 'Out of range' };   // (no round laid: nothing spent)
  const structures = spend(strike.rounds.length);
  return { state: { ...state, structures, airstrikes: { ...a, inbound: [...a.inbound, strike], next: id + 1 } }, strike };
}
